import React, { Component, ReactNode } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { captureError } from "@/lib/sentry";
import { isChunkLoadError, markChunkReload } from "@/lib/lazy-with-retry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

const i18n: Record<string, Record<string, string>> = {
  en: {
    title: "Something went wrong",
    description: "The app encountered an unexpected error. Don't worry — you can retry or go back to the home screen.",
    retry: "Retry",
    home: "Go to home",
    updateTitle: "New version available",
    updateDescription: "A new version of the app was released while this tab was open. Reload to load the latest version.",
    updateAction: "Reload app",
  },
  es: {
    title: "Algo salió mal",
    description: "La aplicación encontró un error inesperado. No te preocupes, puedes reintentar o volver al inicio.",
    retry: "Reintentar",
    home: "Volver al inicio",
    updateTitle: "Nueva versión disponible",
    updateDescription: "Se publicó una nueva versión de la aplicación mientras esta pestaña estaba abierta. Recarga para cargar la última versión.",
    updateAction: "Recargar aplicación",
  },
};

function getTexts(): Record<string, string> {
  try {
    const lang = navigator.language?.slice(0, 2) || "en";
    return i18n[lang] || i18n.en;
  } catch {
    return i18n.en;
  }
}

/**
 * Error Boundary para capturar errores de React
 * Previene que toda la app se caiga por un error en un componente
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    console.error('🔴 [ErrorBoundary] Error caught:', error);
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🔴 [ErrorBoundary] Component stack:', errorInfo.componentStack);
    captureError(error, { componentStack: errorInfo.componentStack });
    this.setState({ error, errorInfo });
  }

  handleReloadForUpdate = () => {
    markChunkReload();
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    const authKeysToPreserve = [
      'sb-zldkeibjkpjfrsxzogln-auth-token',
      'supabase.auth.token',
      'mblearn_session_id',
      'lastLoginTime'
    ];
    const preservedItems: Record<string, string> = {};
    authKeysToPreserve.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) preservedItems[key] = value;
    });
    localStorage.clear();
    sessionStorage.clear();
    Object.entries(preservedItems).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      const txt = getTexts();
      const isStaleBundle = isChunkLoadError(this.state.error);
      if (isStaleBundle) {
        return (
          <div className="min-h-screen w-full bg-gradient-to-br from-background via-background to-secondary/30 flex items-center justify-center p-4">
            <Card className="max-w-md w-full p-8 text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <RefreshCw className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-3">{txt.updateTitle}</h2>
              <p className="text-muted-foreground mb-6">{txt.updateDescription}</p>
              <Button onClick={this.handleReloadForUpdate} size="lg" className="w-full">
                <RefreshCw className="w-5 h-5 mr-2" />
                {txt.updateAction}
              </Button>
            </Card>
          </div>
        );
      }
      return (
        <div className="min-h-screen w-full bg-gradient-to-br from-background via-background to-secondary/30 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold mb-3">{txt.title}</h2>
            <p className="text-muted-foreground mb-6">{txt.description}</p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-destructive/5 rounded-lg text-left">
                <p className="text-sm font-mono text-destructive">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
            <div className="flex flex-col gap-3">
              <Button onClick={this.handleReset} size="lg" className="w-full">
                <RefreshCw className="w-5 h-5 mr-2" />
                {txt.retry}
              </Button>
              <Button onClick={this.handleGoHome} variant="outline" size="lg" className="w-full">
                <Home className="w-5 h-5 mr-2" />
                {txt.home}
              </Button>
            </div>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}